import { packageLabel, PACKAGES } from '@/lib/realEstatePackages';
import { IncludesList } from '@/components/booking/homeSteps';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ordersAPI, filesAPI, revisionsAPI, paymentsAPI } from '@/lib/api';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, Loader2, AlertCircle, Save, Upload, Download, Trash2, 
  FileImage, Clock, CheckCircle, RefreshCw, X, File, Info, Eye, MessageSquare, ThumbsUp, AlertTriangle, CreditCard, DollarSign, FileEdit, Camera, ExternalLink, Images, MapPin, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { startZipDownload } from '@/lib/zipDownload';
import { ScheduledShootCard, useCalendarLinks } from '@/components/scheduling/AddToCalendar';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

// Service type labels
const SERVICE_TYPE_LABELS = {
  photo_restoration: 'Photo Restoration',
  lora_training: 'LoRA Training',
  photo_scanning: 'Photo Scanning',
  digital_persona: 'Digital Persona',
  general_upload: 'General Service',
  real_estate: 'Real Estate',
};

// Service detail field labels
const SERVICE_DETAIL_LABELS = {
  photo_restoration: {
    restoration_level: 'Restoration Level',
    colorization: 'Colorization',
    output_format: 'Output Format',
    special_instructions: 'Special Instructions',
  },
  lora_training: {
    character_name: 'Character/Subject Name',
    style_direction: 'Style Direction',
    intended_use: 'Intended Use',
    notes: 'Additional Notes',
  },
  photo_scanning: {
    estimated_photo_count: 'Estimated Photo Count',
    dpi: 'Scan Resolution (DPI)',
    color_mode: 'Color Mode',
    return_shipping_required: 'Return Shipping',
    notes: 'Additional Notes',
  },
};

// Status config
const STATUS_CONFIG = {
  requested: { label: 'Requested', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  shot: { label: 'Shot', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  editing: { label: 'Editing', className: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  canceled: { label: 'Canceled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  draft: { label: 'Draft', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  pending: { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  paid: { label: 'Paid', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  review: { label: 'In Review', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  completed: { label: 'Completed', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  cancelled: { label: 'Cancelled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

// Revision status config
const REVISION_STATUS_CONFIG = {
  pending: { label: 'Pending Review', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  approved: { label: 'Approved', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  changes_requested: { label: 'Changes Requested', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: RefreshCw },
};

// Revision kind labels
const REVISION_KIND_LABELS = {
  preview: 'Preview',
  final: 'Final',
  reference_sheet: 'Reference Sheet',
  output: 'Output',
};

// File upload states
const UPLOAD_STATES = {
  pending: 'pending',
  uploading: 'uploading',
  confirming: 'confirming',
  done: 'done',
  error: 'error',
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Helper to format detail values for display
function formatDetailValue(key, value, serviceType) {
  if (value === null || value === undefined || value === '') return '—';
  
  // Boolean fields
  if (typeof value === 'boolean') {
    if (key === 'colorization') return value ? 'Yes - Add color' : 'No';
    if (key === 'return_shipping_required') return value ? 'Yes' : 'No';
    return value ? 'Yes' : 'No';
  }
  
  // Enum/select fields with nice labels
  const enumLabels = {
    restoration_level: { basic: 'Basic', standard: 'Standard', premium: 'Premium' },
    output_format: { digital: 'Digital Only', print: 'Print Ready', both: 'Both' },
    style_direction: { realistic: 'Realistic', cinematic: 'Cinematic', fashion: 'Fashion', anime: 'Anime' },
    intended_use: { personal: 'Personal', commercial: 'Commercial' },
    color_mode: { color: 'Color', bw: 'Black & White' },
  };
  
  if (enumLabels[key] && enumLabels[key][value]) {
    return enumLabels[key][value];
  }
  
  // DPI formatting
  if (key === 'dpi') return `${value} DPI`;
  
  return String(value);
}

// ServiceDetailsCard component
function ServiceDetailsCard({ order }) {
  const serviceType = order?.service_type;
  const details = order?.details;
  const fieldLabels = SERVICE_DETAIL_LABELS[serviceType];
  
  if (!fieldLabels || !details) return null;
  
  const fields = Object.keys(fieldLabels);
  
  return (
    <Card className="bg-[#0f1724] border-white/10" data-testid="service-details-card">
      <CardHeader>
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <Info className="h-5 w-5 text-[#C6A14A]" />
          Service Details
        </CardTitle>
        <CardDescription className="text-[#E8E4DE]/60">
          Intake form preferences for this order
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((key) => (
            <div key={key} className="space-y-1">
              <p className="text-xs text-[#E8E4DE]/50 uppercase tracking-wide">
                {fieldLabels[key]}
              </p>
              <p className="text-sm text-white">
                {formatDetailValue(key, details[key], serviceType)}
              </p>
            </div>
          ))}
        </div>
        {order.intake_complete !== undefined && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#E8E4DE]/50 uppercase tracking-wide">Intake Status:</span>
              <Badge className={order.intake_complete 
                ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
              }>
                {order.intake_complete ? 'Complete' : 'In Progress'}
              </Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const DiscountNote = ({ order }) => {
  if (!(order.discount_cents > 0)) return null;
  return (
    <p className="text-sm text-green-400 mb-3" data-testid="order-discount-line">
      Discount{order.discount_code ? ` (${order.discount_code})` : ''}: -${(order.discount_cents / 100).toFixed(2)}
      {order.amount_paid_cents != null && (
        <span className="text-[#E8E4DE]/60"> · Paid ${(order.amount_paid_cents / 100).toFixed(2)}</span>
      )}
    </p>
  );
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const calendarLinks = useCalendarLinks(orderId);
  const navigate = useNavigate();
  
  // Order state
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Files state
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  
  // Upload state
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Revisions state
  const [revisions, setRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [changeFeedback, setChangeFeedback] = useState('');
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [submittingChanges, setSubmittingChanges] = useState(false);

  // Payment state
  const [checkoutBanner, setCheckoutBanner] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Gallery state (for real estate orders)
  const [galleryData, setGalleryData] = useState(null);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Check for checkout return params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutResult = params.get('checkout');
    const sessionId = params.get('session_id');
    
    if (checkoutResult === 'success' && sessionId) {
      setCheckoutBanner('success');
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkoutResult === 'cancel') {
      setCheckoutBanner('cancel');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Poll payment status after checkout
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      setPollingPayment(false);
      toast.info('Payment verification timed out. Please refresh the page.');
      return;
    }
    
    setPollingPayment(true);
    
    try {
      const status = await paymentsAPI.getStatusBySession(sessionId);
      
      if (status.payment_state === 'paid') {
        setPollingPayment(false);
        await loadOrder();
        toast.success('Payment confirmed!', {
          description: 'You can now submit your order.',
        });
        return;
      } else if (status.payment_state === 'failed') {
        setPollingPayment(false);
        toast.error('Payment failed', {
          description: 'Please try again.',
        });
        return;
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Payment status check failed:', err);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  // Handle payment initiation - ALWAYS creates a fresh session
  const handlePayment = async () => {
    try {
      setPaymentProcessing(true);
      console.log('[Payment] Creating fresh checkout session for order:', orderId);
      
      const { checkout_url, session_id } = await paymentsAPI.createCheckoutSession(orderId);
      
      console.log('[Payment] Checkout session created:', session_id);
      console.log('[Payment] Redirecting to checkout_url immediately...');
      
      // IMMEDIATE redirect - no delays, no storing URL in state
      window.location.href = checkout_url;
    } catch (err) {
      console.error('[Payment] Failed to create checkout session:', err);
      toast.error('Failed to start payment', {
        description: err.message || 'Please try again.',
      });
      setPaymentProcessing(false);
    }
  };

  // Load order on mount
  useEffect(() => {
    loadOrder();
  }, [orderId]);

  // Load files and revisions after order loads (skip for real estate)
  useEffect(() => {
    if (order && order.service_type !== 'real_estate') {
      loadFiles();
      loadRevisions();
    }
  }, [order?.id, order?.service_type]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersAPI.get(orderId);
      setOrder(data);
      setNotesValue(data.notes || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      setFilesLoading(true);
      const data = await filesAPI.listForOrder(orderId);
      setFiles(data);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  const loadRevisions = async () => {
    try {
      setLoadingRevisions(true);
      const data = await revisionsAPI.listForOrder(orderId);
      setRevisions(data);
    } catch (err) {
      console.error('Failed to load revisions:', err);
    } finally {
      setLoadingRevisions(false);
    }
  };

  // Load gallery data for real estate orders
  const loadGallery = async (gallerySlug) => {
    if (!gallerySlug) return;
    try {
      setGalleryLoading(true);
      const response = await authFetch(`${API_URL}/api/gallery/property/${gallerySlug}`);
      if (response.ok) {
        const data = await response.json();
        setGalleryData(data);
      }
    } catch (err) {
      console.error('Failed to load gallery:', err);
    } finally {
      setGalleryLoading(false);
    }
  };

  // Load gallery when order loads (for real estate orders)
  useEffect(() => {
    if (order?.service_type === 'real_estate' && order?.gallery_slug) {
      loadGallery(order.gallery_slug);
    }
  }, [order?.gallery_slug, order?.service_type]);

  // Download all photos as one ZIP streamed by the backend
  const handleDownloadAll = async () => {
    if (!order?.gallery_slug) return;
    setDownloadingAll(true);
    await startZipDownload(() =>
      authFetch(`${API_URL}/api/gallery/property/${order.gallery_slug}/zip-ticket`, { method: 'POST' })
    );
    setDownloadingAll(false);
  };

  const handleApproveRevision = async (revisionId) => {
    try {
      setSubmittingApproval(true);
      await revisionsAPI.approve(revisionId);
      
      toast.success('Revision approved!', {
        description: 'Thank you for your approval.',
      });
      
      // Refresh data
      await loadRevisions();
      await loadOrder();
    } catch (err) {
      toast.error('Failed to approve revision', {
        description: err.message,
      });
    } finally {
      setSubmittingApproval(false);
    }
  };

  const handleRequestChanges = async (revisionId) => {
    if (changeFeedback.trim().length < 3) {
      toast.error('Please provide feedback', {
        description: 'Feedback must be at least 3 characters.',
      });
      return;
    }
    
    try {
      setSubmittingChanges(true);
      const result = await revisionsAPI.requestChanges(revisionId, changeFeedback.trim());
      
      toast.success('Changes requested', {
        description: `Revision requests used: ${result.revision_requests_used}/${result.max_revisions}`,
      });
      
      setChangeFeedback('');
      
      // Refresh data
      await loadRevisions();
      await loadOrder();
    } catch (err) {
      toast.error('Failed to request changes', {
        description: err.message,
      });
    } finally {
      setSubmittingChanges(false);
    }
  };

  const handleDownloadRevision = (revision) => {
    if (revision.download_url) {
      window.open(revision.download_url, '_blank');
    } else {
      toast.error('Download not available', {
        description: 'The file is not ready for download yet.',
      });
    }
  };

  const saveNotes = async () => {
    try {
      setSavingNotes(true);
      await ordersAPI.update(orderId, { notes: notesValue });
      setOrder(prev => ({ ...prev, notes: notesValue }));
      setEditingNotes(false);
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newQueue = selectedFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      state: UPLOAD_STATES.pending,
      progress: 0,
      error: null,
    }));
    setUploadQueue(prev => [...prev, ...newQueue]);
    
    // Clear input
    e.target.value = '';
  };

  const processUploadQueue = async () => {
    if (uploading) return;
    
    const pendingItems = uploadQueue.filter(item => item.state === UPLOAD_STATES.pending);
    if (pendingItems.length === 0) return;
    
    setUploading(true);
    
    for (const item of pendingItems) {
      try {
        // Update state to uploading
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.uploading, progress: 0 } : q
        ));
        
        // Step 1: Get presigned URL
        const uploadInfo = await filesAPI.getUploadUrl(
          orderId,
          'reference',
          item.file.name,
          item.file.type,
          item.file.size
        );
        
        // Step 2: Upload to S3
        await filesAPI.uploadToS3(uploadInfo.upload_url, item.file, (progress) => {
          setUploadQueue(prev => prev.map(q => 
            q.id === item.id ? { ...q, progress } : q
          ));
        });
        
        // Update state to confirming
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.confirming, progress: 100 } : q
        ));
        
        // Step 3: Confirm upload
        await filesAPI.confirmUpload(uploadInfo.file_id);
        
        // Mark as done
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.done } : q
        ));
        
      } catch (err) {
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, state: UPLOAD_STATES.error, error: err.message } : q
        ));
      }
    }
    
    setUploading(false);
    
    // Refresh file list
    await loadFiles();
    
    // Clear completed uploads after a delay
    setTimeout(() => {
      setUploadQueue(prev => prev.filter(q => q.state !== UPLOAD_STATES.done));
    }, 2000);
  };

  // Auto-process queue when items are added
  useEffect(() => {
    const hasPending = uploadQueue.some(item => item.state === UPLOAD_STATES.pending);
    if (hasPending && !uploading) {
      processUploadQueue();
    }
  }, [uploadQueue, uploading]);

  const removeFromQueue = (id) => {
    setUploadQueue(prev => prev.filter(q => q.id !== id));
  };

  const retryUpload = (item) => {
    setUploadQueue(prev => prev.map(q => 
      q.id === item.id ? { ...q, state: UPLOAD_STATES.pending, error: null, progress: 0 } : q
    ));
  };

  const handleDownload = async (file) => {
    try {
      const { download_url } = await filesAPI.getDownloadUrl(orderId, file.id);
      window.open(download_url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await filesAPI.delete(file.id);
      await loadFiles();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // Separate reference and output files
  const referenceFiles = files.filter(f => f.kind === 'reference');
  const outputFiles = files.filter(f => f.kind === 'output');

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#050816] flex flex-col items-center justify-center text-center px-4">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg text-white mb-2">Failed to load order</h3>
        <p className="text-[#E8E4DE]/60 mb-4">{error}</p>
        <Button asChild variant="outline" className="border-white/10 text-[#E8E4DE]">
          <Link to="/orders">Back to Orders</Link>
        </Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Button 
            asChild
            variant="ghost" 
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/orders">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Orders
            </Link>
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-light text-white tracking-wide">
                  {order.title || SERVICE_TYPE_LABELS[order.service_type] || order.service_type}
                </h1>
                <Badge className={statusConfig.className}>
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-[#E8E4DE]/60">
                {SERVICE_TYPE_LABELS[order.service_type] || order.service_type} • Created {formatDate(order.created_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Delivered Banner - Points to final files */}
        {order.status === 'delivered' && (
          <div 
            className="p-4 bg-green-500/15 border-2 border-green-500/40 rounded-lg flex items-start gap-4"
            data-testid="delivered-banner"
          >
            <div className="p-2 bg-green-500/20 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <div className="flex-1">
              <p className="text-green-400 font-semibold text-lg">Delivered</p>
              <p className="text-[#E8E4DE]/80 mt-1">
                Final files are ready for download below.
              </p>
              <p className="text-sm text-[#E8E4DE]/50 mt-2">
                <Download className="h-3 w-3 inline mr-1" />
                Scroll to the <span className="text-green-400">Deliverables</span> section to access your files.
              </p>
            </div>
          </div>
        )}

        {order.service_type === 'real_estate' && order.status === 'scheduled' && (
          <ScheduledShootCard links={calendarLinks} prefix="client-calendar" />
        )}

        {/* Real Estate Gallery Section - For real_estate orders */}
        {order.service_type === 'real_estate' && (
          <Card className="bg-[#0f1724] border-white/10" data-testid="real-estate-gallery-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Camera className="h-5 w-5 text-[#C6A14A]" />
                    Property Gallery
                  </CardTitle>
                  <CardDescription className="text-[#E8E4DE]/60">
                    {order.details?.property_address || 'View your property photos'}
                  </CardDescription>
                </div>
                {/* Order Reference */}
                <div className="text-right">
                  <p className="text-xs text-[#E8E4DE]/40 uppercase tracking-wider">Order</p>
                  <p className="text-sm font-mono text-[#C6A14A]">
                    RE-{order.id?.split('-')[0]?.toUpperCase()}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Property Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[#050816]/30 rounded-lg">
                {order.details?.property_address && (
                  <div>
                    <p className="text-xs text-[#E8E4DE]/40 uppercase tracking-wider mb-1">Address</p>
                    <p className="text-sm text-white flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-[#C6A14A]" />
                      {order.details.property_address}
                    </p>
                    {order.details?.property_city && (
                      <p className="text-xs text-[#E8E4DE]/50">{order.details.property_city}, {order.details.property_state}</p>
                    )}
                  </div>
                )}
                {order.details?.package && (
                  <div className="col-span-2">
                    <p className="text-xs text-[#E8E4DE]/40 uppercase tracking-wider mb-1">Package</p>
                    <p className="text-sm text-[#C6A14A] font-medium">
                      {packageLabel(order.details.package)}
                    </p>
                    {PACKAGES[order.details.package] && <IncludesList pkg={PACKAGES[order.details.package]} testId="order-package-includes" />}
                  </div>
                )}
                {order.shoot_date && (
                  <div>
                    <p className="text-xs text-[#E8E4DE]/40 uppercase tracking-wider mb-1">Shoot Date</p>
                    <p className="text-sm text-white flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-[#C6A14A]" />
                      {formatDate(order.shoot_date)}
                    </p>
                  </div>
                )}
                {galleryData?.photos?.length > 0 && (
                  <div>
                    <p className="text-xs text-[#E8E4DE]/40 uppercase tracking-wider mb-1">Photos</p>
                    <p className="text-sm text-white flex items-center gap-1">
                      <Images className="h-3 w-3 text-[#C6A14A]" />
                      {galleryData.photos.length} photos
                    </p>
                  </div>
                )}
              </div>

              {/* Gallery Cover Image (if available) */}
              {galleryData?.photos?.length > 0 && (
                <div className="relative rounded-lg overflow-hidden aspect-video bg-[#050816]">
                  <img 
                    src={galleryData.photos[0]?.watermarked_url || galleryData.photos[0]?.thumbnail_url}
                    alt={order.details?.property_address || 'Property'}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <p className="text-white font-medium">{order.details?.property_address}</p>
                    <p className="text-white/70 text-sm">{galleryData.photos.length} photos available</p>
                  </div>
                </div>
              )}

              {/* Gallery Actions */}
              <div className="flex flex-wrap gap-3">
                {order.gallery_slug && (
                  <Button
                    asChild
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                    data-testid="view-gallery-btn"
                  >
                    <Link to={`/property/${order.gallery_slug}`}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Gallery
                    </Link>
                  </Button>
                )}
                
                {/* Download All - Only when paid */}
                {(order.status === 'paid' || order.payment_state === 'paid') && order.gallery_slug && galleryData?.photos?.length > 0 && (
                  <Button
                    onClick={handleDownloadAll}
                    disabled={downloadingAll}
                    variant="outline"
                    className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    data-testid="download-all-zip-btn"
                  >
                    {downloadingAll ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    {downloadingAll ? 'Preparing your photos…' : `Download All Photos (${galleryData.photos.length}) · ZIP`}
                  </Button>
                )}
                
                {/* Invoice Link */}
                {order.invoice_id && (
                  <Button
                    asChild
                    variant="outline"
                    className="border-white/10 text-[#E8E4DE] hover:bg-white/5"
                    data-testid="view-invoice-btn"
                  >
                    <Link to={`/invoice/${order.invoice_id}`}>
                      <CreditCard className="h-4 w-4 mr-2" />
                      {(order.payment_state === 'paid' || order.status === 'paid') ? 'View Invoice' : 'Pay Invoice'}
                    </Link>
                  </Button>
                )}
              </div>

              {/* Payment Status */}
              <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                <span className="text-xs text-[#E8E4DE]/40 uppercase tracking-wider">Payment:</span>
                <Badge data-testid="re-payment-badge" className={
                  (order.payment_state === 'paid' || order.status === 'paid')
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                }>
                  {(order.payment_state === 'paid' || order.status === 'paid') ? 'Paid' : 'Pending'}
                </Badge>
                {order.amount_paid_cents != null && (
                  <span className="text-[#C6A14A] font-medium ml-auto" data-testid="order-amount-paid">
                    ${(order.amount_paid_cents / 100).toFixed(2)}
                  </span>
                )}
                {order.amount_paid_cents == null && order.invoice_total_cents && (
                  <span className="text-[#C6A14A] font-medium ml-auto">
                    ${(order.invoice_total_cents / 100).toFixed(2)}
                  </span>
                )}
                {order.amount_paid_cents == null && !order.invoice_total_cents && order.details?.total_price_cents && (
                  <span className="text-[#C6A14A] font-medium ml-auto">
                    ${(order.details.total_price_cents / 100).toFixed(2)}
                  </span>
                )}
              </div>
              <DiscountNote order={order} />

              {/* Download locked message */}
              {order.payment_state !== 'paid' && order.status !== 'paid' && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-sm text-yellow-400 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Full resolution downloads will be available after payment is complete.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Success Banner - Shows when payment is complete and order is received */}
        {order.payment_state === 'paid' && order.status === 'received' && (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg" data-testid="payment-received-banner">
            <CheckCircle className="h-5 w-5 text-green-400 shrink-0" />
            <div className="flex-1">
              <p className="text-green-400 font-medium">Payment received — your order is now in production.</p>
              <p className="text-green-400/70 text-sm">We'll notify you when there are updates.</p>
            </div>
          </div>
        )}

        {/* Checkout Result Banners */}
        {checkoutBanner === 'success' && order.status !== 'received' && (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg" data-testid="checkout-success-banner">
            <CheckCircle className="h-5 w-5 text-green-400 shrink-0" />
            <div className="flex-1">
              <p className="text-green-400 font-medium">Payment successful!</p>
              <p className="text-green-400/70 text-sm">
                {pollingPayment ? 'Verifying payment...' : 'Your order has been submitted.'}
              </p>
            </div>
            {pollingPayment && <Loader2 className="h-5 w-5 text-green-400 animate-spin" />}
          </div>
        )}

        {checkoutBanner === 'cancel' && (
          <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg" data-testid="checkout-cancel-banner">
            <AlertCircle className="h-5 w-5 text-yellow-400 shrink-0" />
            <div className="flex-1">
              <p className="text-yellow-400 font-medium">Payment canceled</p>
              <p className="text-yellow-400/70 text-sm">Your order is still in draft. You can try again when ready.</p>
            </div>
          </div>
        )}

        {/* Payment Section - Only show for draft orders */}
        {order.status === 'draft' && (
          <Card className="bg-[#0f1724] border-white/10" data-testid="payment-section">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-[#C6A14A]" />
                Payment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Payment status info */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[#E8E4DE]/60 text-sm">Payment Status:</span>
                <Badge className={
                  order.payment_state === 'paid' 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : order.payment_state === 'pending'
                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    : order.payment_state === 'failed'
                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                }>
                  {order.payment_state === 'paid' ? 'Paid' :
                   order.payment_state === 'pending' ? 'Pending' :
                   order.payment_state === 'failed' ? 'Failed' : 'Unpaid'}
                </Badge>
                {order.amount_total_cents && (
                  <span className="text-[#C6A14A] font-medium ml-2">
                    ${(order.amount_total_cents / 100).toFixed(2)}
                  </span>
                )}
              </div>
              <DiscountNote order={order} />

              {order.payment_state === 'failed' ? (
                // Payment failed - show retry option
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <span className="text-white font-medium">Payment Failed</span>
                    </div>
                    <p className="text-red-400/80 text-sm">
                      There was an issue with your payment. Please try again.
                    </p>
                  </div>
                  <Button
                    onClick={handlePayment}
                    disabled={!order.intake_complete || paymentProcessing}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                    data-testid="retry-payment-btn"
                  >
                    {paymentProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Try Again
                  </Button>
                </div>
              ) : (
                // Unpaid - show Continue to Payment flow
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-white font-medium">Ready to proceed?</span>
                      {!order.intake_complete && (
                        <div className="mt-2">
                          <p className="text-yellow-400/80 text-sm">
                            Complete your intake form before proceeding to payment.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 border-[#C6A14A]/50 text-[#C6A14A] hover:bg-[#C6A14A]/10"
                            onClick={() => {
                              // Navigate to service-specific intake form
                              const serviceRoutes = {
                                'photo_restoration': '/order/photo-restoration',
                                'lora_training': '/order/lora-training',
                                'photo_scanning': '/order/photo-scanning',
                              };
                              const route = serviceRoutes[order.service_type];
                              if (route) {
                                navigate(`${route}?orderId=${order.id}`);
                              }
                            }}
                            data-testid="complete-intake-btn"
                          >
                            <FileEdit className="h-4 w-4 mr-2" />
                            Complete Intake Form
                          </Button>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handlePayment}
                      disabled={!order.intake_complete || paymentProcessing}
                      className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] disabled:opacity-50"
                      data-testid="continue-to-payment-btn"
                    >
                      {paymentProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="h-4 w-4 mr-2" />
                      )}
                      Continue to Payment
                    </Button>
                  </div>
                  
                  <div className="flex items-start gap-3 p-3 bg-[#050816]/30 rounded-lg border border-white/5">
                    <DollarSign className="h-5 w-5 text-[#C6A14A] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#E8E4DE]/60">
                      You'll be redirected to our secure payment portal (Stripe). Your order will be submitted automatically after successful payment.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Details */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white">Order Notes</CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              Add any special instructions or details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {editingNotes ? (
              <div className="space-y-3">
                <Textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  placeholder="Add notes about your order..."
                  rows={4}
                  className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A] resize-none"
                  data-testid="order-notes-textarea"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={saveNotes}
                    disabled={savingNotes}
                    size="sm"
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                  >
                    {savingNotes ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="mr-1 h-3 w-3" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setNotesValue(order.notes || '');
                      setEditingNotes(false);
                    }}
                    size="sm"
                    variant="outline"
                    className="border-white/10 text-[#E8E4DE]"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                onClick={() => setEditingNotes(true)}
                className="min-h-[80px] p-3 bg-[#050816]/30 rounded-lg cursor-pointer hover:bg-[#050816]/50 transition-colors"
              >
                {order.notes ? (
                  <p className="text-[#E8E4DE] whitespace-pre-wrap">{order.notes}</p>
                ) : (
                  <p className="text-[#E8E4DE]/40 italic">Click to add notes...</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Service Details Section */}
        <ServiceDetailsCard order={order} />

        {/* Upload Section - Hide for real estate orders */}
        {order.service_type !== 'real_estate' && (
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Upload className="h-5 w-5 text-[#C6A14A]" />
                  Reference Files
                </CardTitle>
                <CardDescription className="text-[#E8E4DE]/60">
                  {['in_progress', 'delivered', 'canceled'].includes(order.status)
                    ? 'Uploads are locked once processing has begun'
                    : 'Upload photos and files for your order'
                  }
                </CardDescription>
              </div>
              {!['in_progress', 'delivered', 'canceled'].includes(order.status) ? (
                <div className="relative">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="file-upload-input"
                  />
                  <Button className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]">
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Files
                  </Button>
                </div>
              ) : (
                <Button disabled className="bg-gray-500/20 text-gray-400 cursor-not-allowed">
                  <Upload className="mr-2 h-4 w-4" />
                  Uploads Locked
                </Button>
              )}
            </div>
            {['in_progress', 'delivered', 'canceled'].includes(order.status) && (
              <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-400/80">
                  Uploads are locked once processing has begun. Contact support if you need to add files.
                </p>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Upload Queue */}
            {uploadQueue.length > 0 && (
              <div className="space-y-2 mb-4">
                {uploadQueue.map((item) => (
                  <div 
                    key={item.id}
                    className="flex items-center gap-3 p-3 bg-[#050816]/50 rounded-lg"
                  >
                    <FileImage className="h-5 w-5 text-[#C6A14A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.file.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {item.state === UPLOAD_STATES.uploading && (
                          <>
                            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#C6A14A] transition-all duration-300"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-[#E8E4DE]/60">{item.progress}%</span>
                          </>
                        )}
                        {item.state === UPLOAD_STATES.confirming && (
                          <span className="text-xs text-[#C6A14A] flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Confirming...
                          </span>
                        )}
                        {item.state === UPLOAD_STATES.done && (
                          <span className="text-xs text-green-400 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Uploaded
                          </span>
                        )}
                        {item.state === UPLOAD_STATES.error && (
                          <span className="text-xs text-red-400">{item.error}</span>
                        )}
                      </div>
                    </div>
                    {item.state === UPLOAD_STATES.error && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => retryUpload(item)}
                        className="text-[#C6A14A] hover:text-[#D4AF5A] hover:bg-white/5"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                    {(item.state === UPLOAD_STATES.pending || item.state === UPLOAD_STATES.error) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromQueue(item.id)}
                        className="text-[#E8E4DE]/40 hover:text-red-400 hover:bg-white/5"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* File List */}
            {filesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-[#C6A14A] animate-spin" />
              </div>
            ) : referenceFiles.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-white/10 rounded-lg">
                <FileImage className="h-10 w-10 text-[#E8E4DE]/30 mx-auto mb-3" />
                <p className="text-[#E8E4DE]/60">No files uploaded yet</p>
                <p className="text-sm text-[#E8E4DE]/40 mt-1">
                  Upload reference photos for your order
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {referenceFiles.map((file) => (
                  <div 
                    key={file.id}
                    className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg group"
                  >
                    <FileImage className="h-5 w-5 text-[#C6A14A] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{file.original_filename}</p>
                      <p className="text-xs text-[#E8E4DE]/40">
                        {formatFileSize(file.size_bytes)} • {formatDate(file.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(file)}
                        className="text-[#C6A14A] hover:text-[#D4AF5A] hover:bg-white/5"
                        data-testid={`download-file-${file.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {!['in_progress', 'delivered', 'canceled'].includes(order.status) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(file)}
                          className="text-[#E8E4DE]/40 hover:text-red-400 hover:bg-white/5"
                          data-testid={`delete-file-${file.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Outputs/Deliverables Section - Emphasized when delivered, hide for real estate (they have gallery) */}
        {order.service_type !== 'real_estate' && (outputFiles.length > 0 || order.status === 'delivered') && (
          <Card 
            className={`border-white/10 ${
              order.status === 'delivered' 
                ? 'bg-green-500/5 border-green-500/30 ring-1 ring-green-500/20' 
                : 'bg-[#0f1724]'
            }`}
            data-testid="deliverables-section"
          >
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Download className={`h-5 w-5 ${order.status === 'delivered' ? 'text-green-400' : 'text-green-400'}`} />
                {order.status === 'delivered' ? 'Final Deliverables' : 'Deliverables'}
                {order.status === 'delivered' && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 ml-2">
                    Final
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-[#E8E4DE]/60">
                {order.status === 'delivered' 
                  ? 'Your completed project files — ready to download'
                  : 'Your completed files are ready for download'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {outputFiles.length > 0 ? (
                <div className="space-y-2">
                  {outputFiles.map((file) => (
                    <div 
                      key={file.id}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        order.status === 'delivered'
                          ? 'bg-green-500/10 border border-green-500/30'
                          : 'bg-green-500/5 border border-green-500/20'
                      }`}
                      data-testid={`output-file-${file.id}`}
                    >
                      <File className="h-5 w-5 text-green-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{file.original_filename}</p>
                        <p className="text-xs text-[#E8E4DE]/40">
                          {formatFileSize(file.size_bytes)} • {formatDate(file.created_at)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleDownload(file)}
                        className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        data-testid={`download-output-${file.id}`}
                      >
                        <Download className="mr-1 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border border-dashed border-yellow-500/30 rounded-lg bg-yellow-500/5">
                  <AlertTriangle className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                  <p className="text-yellow-400 font-medium">Final files have not been uploaded yet.</p>
                  <p className="text-sm text-[#E8E4DE]/60 mt-1">
                    Please contact support for assistance.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Revisions & Approvals Section */}
        {revisions.length > 0 && (
          <Card className="bg-[#0f1724] border-white/10" data-testid="revisions-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Eye className="h-5 w-5 text-[#C6A14A]" />
                    Revisions & Approvals
                  </CardTitle>
                  <CardDescription className="text-[#E8E4DE]/60">
                    Review and approve revisions from our team
                  </CardDescription>
                </div>
                {/* Plan usage display */}
                {order?.plan?.max_revisions > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-[#E8E4DE]/60">Revision Requests</p>
                    <p className="text-sm font-medium text-white">
                      {order?.revision_state?.revision_requests_used || 0} / {order?.plan?.max_revisions}
                    </p>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Revisions list - newest first */}
              {revisions.map((revision, index) => {
                const statusConfig = REVISION_STATUS_CONFIG[revision.status] || REVISION_STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                const kindLabel = REVISION_KIND_LABELS[revision.kind] || revision.kind;
                const isLatestPending = index === 0 && revision.status === 'pending';
                const maxRevisionsReached = order?.plan?.max_revisions > 0 && 
                  (order?.revision_state?.revision_requests_used || 0) >= order?.plan?.max_revisions;
                
                return (
                  <div 
                    key={revision.id}
                    className={`p-4 rounded-lg border ${
                      isLatestPending 
                        ? 'bg-[#C6A14A]/5 border-[#C6A14A]/30' 
                        : 'bg-[#050816]/30 border-white/5'
                    }`}
                    data-testid={`revision-item-${revision.id}`}
                  >
                    {/* Revision Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">
                            Revision #{revision.revision_number}
                          </span>
                          <Badge className={statusConfig.className}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          <Badge variant="outline" className="text-[#C6A14A] border-[#C6A14A]/30">
                            {kindLabel}
                          </Badge>
                        </div>
                        <p className="text-sm text-[#E8E4DE]/60">
                          {revision.original_filename || 'Revision file'}
                        </p>
                        <p className="text-xs text-[#E8E4DE]/40 mt-1">
                          Uploaded {formatDate(revision.created_at)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleDownloadRevision(revision)}
                        className="bg-[#C6A14A]/20 text-[#C6A14A] hover:bg-[#C6A14A]/30"
                        data-testid={`download-revision-${revision.id}`}
                      >
                        <Download className="mr-1 h-4 w-4" />
                        View/Download
                      </Button>
                    </div>

                    {/* Customer feedback (if changes requested) */}
                    {revision.customer_feedback && (
                      <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg mb-3">
                        <div className="flex items-center gap-1 text-orange-400 text-xs mb-1">
                          <MessageSquare className="h-3 w-3" />
                          Your Feedback
                        </div>
                        <p className="text-sm text-[#E8E4DE]/80">{revision.customer_feedback}</p>
                      </div>
                    )}

                    {/* Approval Actions (only for latest pending revision, and NOT if order is delivered) */}
                    {isLatestPending && order.status !== 'delivered' && (
                      <div className="pt-3 border-t border-white/10 space-y-3">
                        <p className="text-sm text-[#C6A14A]">
                          Please review and take action:
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-3">
                          {/* Approve Button */}
                          <Button
                            onClick={() => handleApproveRevision(revision.id)}
                            disabled={submittingApproval || submittingChanges}
                            className="bg-green-600 text-white hover:bg-green-700"
                            data-testid="approve-revision-btn"
                          >
                            {submittingApproval ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <ThumbsUp className="h-4 w-4 mr-2" />
                            )}
                            Approve
                          </Button>
                          
                          {/* Request Changes - with limit check */}
                          <div className="flex-1 space-y-2">
                            {maxRevisionsReached ? (
                              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                <div className="flex items-center gap-2 text-red-400">
                                  <AlertTriangle className="h-4 w-4" />
                                  <span className="text-sm font-medium">Revision limit reached for your plan.</span>
                                </div>
                                <p className="text-xs text-[#E8E4DE]/60 mt-1">
                                  You've used all {order?.plan?.max_revisions} revision requests. Please approve or contact support.
                                </p>
                              </div>
                            ) : (
                              <>
                                <Textarea
                                  value={changeFeedback}
                                  onChange={(e) => setChangeFeedback(e.target.value)}
                                  placeholder="Describe what changes you'd like..."
                                  rows={2}
                                  className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 resize-none"
                                  disabled={submittingChanges}
                                  data-testid="change-feedback-textarea"
                                />
                                <Button
                                  onClick={() => handleRequestChanges(revision.id)}
                                  disabled={submittingChanges || submittingApproval || changeFeedback.trim().length < 3}
                                  variant="outline"
                                  className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                                  data-testid="request-changes-btn"
                                >
                                  {submittingChanges ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                  )}
                                  Request Changes
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Delivered Lock Notice - Shows when order is delivered and revision was pending */}
                    {isLatestPending && order.status === 'delivered' && (
                      <div className="pt-3 border-t border-white/10">
                        <div className="p-3 bg-[#050816]/50 border border-white/10 rounded-lg">
                          <p className="text-sm text-[#E8E4DE]/70">
                            <Info className="h-4 w-4 inline mr-2 text-[#E8E4DE]/50" />
                            This order has been marked <span className="text-green-400 font-medium">Delivered</span>. 
                            Contact support if further changes are needed.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Approved indicator */}
                    {revision.status === 'approved' && revision.approved_at && (
                      <div className="pt-3 border-t border-white/10">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm">Approved on {formatDate(revision.approved_at)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
